/**
 * Indian number formatting – reusable across forms (BidRequestModal, CreateListProperty, etc.)
 */

/** Format number for display in Indian style (e.g. 500000 → "5,00,000"). Handles decimals. */
export function formatIndianNumber(val: string | number | undefined | null): string {
  if (val === undefined || val === null || val === "") return ""
  const s = String(val).trim().replace(/,/g, "")
  if (!s) return ""
  const parts = s.split(".")
  const intPart = parts[0].replace(/\D/g, "") || "0"
  const decPart = parts[1]
  if (intPart.length <= 3) return decPart !== undefined ? `${intPart}.${decPart || ""}` : intPart
  let result = intPart.slice(-3)
  for (let i = intPart.length - 3; i > 0; i -= 2) {
    result = intPart.slice(Math.max(0, i - 2), i) + "," + result
  }
  return decPart !== undefined ? `${result}.${decPart}` : result
}

/** Parse display string with Indian commas back to raw string for storage/calculations. */
export function parseIndianNumber(str: string | undefined | null): string {
  if (str === undefined || str === null) return ""
  return String(str).replace(/,/g, "").trim()
}

/** True when value is a plain number (not mixed text like "b + G + 4"). */
export function isNumericDisplayValue(val: unknown): boolean {
  if (val === null || val === undefined) return false
  if (typeof val === "number") return Number.isFinite(val)
  const s = String(val).trim().replace(/,/g, "")
  if (!s) return false
  return /^-?\d+(\.\d+)?$/.test(s)
}

/** Format numbers for read-only UI with Indian commas; pass through non-numeric text. */
export function formatDisplayNumber(
  val: string | number | undefined | null,
  options?: {
    prefix?: string
    suffix?: string
    maximumFractionDigits?: number
    fallback?: string
  }
): string {
  const fallback = options?.fallback ?? "—"
  if (val === undefined || val === null || val === "") return fallback
  if (!isNumericDisplayValue(val)) return String(val).trim()
  const num = typeof val === "number" ? val : Number(parseIndianNumber(String(val)))
  if (!Number.isFinite(num)) return String(val).trim()
  const formatted = num.toLocaleString(
    "en-IN",
    options?.maximumFractionDigits != null ? { maximumFractionDigits: options.maximumFractionDigits } : undefined
  )
  const withPrefix = options?.prefix ? `${options.prefix}${formatted}` : formatted
  return options?.suffix ? `${withPrefix} ${options.suffix}` : withPrefix
}

const AREA_KEYS_SQ_FT = new Set([
  "plotArea",
  "superAreaSqFt",
  "totalCarpetArea",
  "superArea",
  "carpetArea",
  "builtUpArea",
  "superBuiltUpArea",
  "balconyArea",
  "terraceArea",
  "carpetAreaSqFt",
  "totalCarpetAreaSqFt",
])

const PRICE_RELATED_KEYS = new Set([
  "propertyTaxMunicipalTax",
  "totalSalePrice",
  "estimatedMonthlyMaintenance",
  "pricePerSqFt",
  "securityDepositTotal",
  "currentGrossMonthlyRentalAllTenants",
  "totalMonthlyMaintenanceCost",
  "pricePerKey",
  "pricePerRoom",
  "totalPriceOffered",
  "rentalPerSqFtMonthly",
  "currentMonthlyRental",
  "monthlyMaintenanceCharges",
  "currentMonthlyRentalAllTenants",
  "budget",
  "price",
  "ratePerSqft",
  "ratePerSqFt",
  "revenueGenerated",
  "transactionValue",
  "bookingAmount",
  "biddingStartPrice",
  "biddingMinIncrement",
  "propertyTaxAnnual",
  "monthlyRent",
  "securityDeposit",
  "investmentAmount",
])

/** Format category-specific / dynamic listing fields for display. */
export function formatCategoryFieldDisplay(key: string, value: unknown): string {
  if (value == null || value === "") return "—"
  if (Array.isArray(value)) {
    return value.map((item) => formatCategoryFieldDisplay(key, item)).join(", ")
  }
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) return parsed.map((item) => formatCategoryFieldDisplay(key, item)).join(", ")
      } catch {
        /* fall through */
      }
    }
  }
  if (!isNumericDisplayValue(value)) return String(value).trim()
  const numericValue: string | number = typeof value === "number" ? value : String(value)
  if (PRICE_RELATED_KEYS.has(key)) {
    const num = typeof value === "number" ? value : Number(parseIndianNumber(String(value)))
    return formatBidPrice(num)
  }
  if (AREA_KEYS_SQ_FT.has(key)) {
    return formatDisplayNumber(numericValue, { suffix: "sq.ft" })
  }
  return formatDisplayNumber(numericValue)
}

/** Format a numeric amount for display with ₹ and Indian locale (e.g. 500000 → "₹5,00,000"). */
export function formatBidPrice(n: number | undefined | null): string {
  if (n == null || !Number.isFinite(n)) return "—"
  return `₹${Number(n).toLocaleString("en-IN")}`
}
